/* eslint-disable import/no-unresolved */
/* eslint-disable react/jsx-props-no-spreading */
/* eslint-disable no-return-assign */
/* eslint-disable react/require-default-props */
import PropTypes from 'prop-types';
import React, {
  useCallback, useContext, useEffect, useMemo, useRef,
  useState,
} from 'react';
import {
  Navigation, A11y, Controller,
} from 'swiper/modules';

import { AppContext } from '@powdr/context';
import { getRandomInt } from '@powdr/utils';
import { useStaticData } from '@powdr/web/src/hooks';

import { OrderedShowcaseItem } from './components';
import {
  StyledOrderedShowcase, ItemWrapper, IndicatorBar,
  IndicatorIcon,
  IndicatorIconWrapper,
  IndicatorText,
  BarElement,
  StyledSwiperSlide,
  StyledSwiper,
  IndicatorButton,
} from './styles';

// TODO: possible change the group size to only control the amount it moves rather
// than using it for settings with Swiper (could improve ability to move around the
// carousel for some use cases)
export const OrderedShowcase = ({
  className = '',
  enableUserScrolling = false,
  activeShowcaseItem = 1,
  is5050 = false,
  itemSizeLimit = null,
  relationships,
  parentColorProfile,
}) => {
  const { property } = useStaticData();
  const { isMobile } = useContext(AppContext);
  const [uniqueId] = useState(getRandomInt(0, 100000));
  const swiperRef = useRef(null);
  const scrollbarRef = useRef(null);

  const orderedShowcaseItems = useMemo(
    () => relationships?.showcaseItems || [],
    [relationships],
  );

  const totalSlides = orderedShowcaseItems.length;
  const groupSize = Math.min(isMobile ? 3 : 5, totalSlides);

  const [indicatorLeft, setIndicatorLeft] = useState(0);
  const [indicatorWidth, setIndicatorWidth] = useState(100 / groupSize);
  const [isDragging, setIsDragging] = useState(false);
  const [selectorIndex, setSelectorIndex] = useState((activeShowcaseItem - 1) % groupSize);
  const [indicatorInformationIndex, setIndicatorInformationIndex] = useState(
    activeShowcaseItem - 1,
  );
  const [slideChangedViaClick, setSlideChangedViaClick] = useState(false);

  const updateIndicatorPosition = useCallback((clampedIndex, size) => {
    const percent = ((clampedIndex + 0.5) / size) * 100;
    setIndicatorLeft(percent);
    setIndicatorWidth(100 / size);
  }, []);

  const handlePointerDown = (e) => {
    if (!scrollbarRef.current.contains(e.target) || !enableUserScrolling) return;
    setIsDragging(true);
  };

  const handlePointerMove = useCallback(
    (e) => {
      if (
        !isDragging
        || !scrollbarRef.current
        || !swiperRef.current
        || !enableUserScrolling
      ) return;

      const swiper = swiperRef.current;
      const rect = scrollbarRef.current.getBoundingClientRect();
      if (e.clientX < rect.left || e.clientX > rect.right) return;

      const offsetX = e.clientX - rect.left;
      const segmentWidth = rect.width / groupSize;
      const segmentIndex = Math.floor(offsetX / segmentWidth);
      const clampedIndex = Math.max(0, Math.min(groupSize - 1, segmentIndex));

      const newIndex = swiper.activeIndex + clampedIndex;
      setIndicatorInformationIndex(Math.min(newIndex, totalSlides - 1));
      setSelectorIndex(clampedIndex);
      updateIndicatorPosition(clampedIndex, groupSize);
    },
    [isDragging, groupSize, updateIndicatorPosition, totalSlides, enableUserScrolling],
  );

  const handlePointerUp = useCallback(() => {
    if (!swiperRef.current || !enableUserScrolling) return;

    const swiper = swiperRef.current;
    const isLastInGroup = selectorIndex === groupSize - 1;
    const isFirstInGroup = selectorIndex === 0;

    if (isLastInGroup) {
      swiper.slideTo(swiper.activeIndex + groupSize);
    } else if (isFirstInGroup) {
      swiper.slideTo(Math.max(swiper.activeIndex - groupSize, 0));
    }
    setIsDragging(false);
  }, [groupSize, selectorIndex, enableUserScrolling]);

  const handleItemClick = useCallback((index) => {
    if (!enableUserScrolling || !swiperRef.current) return;

    const swiper = swiperRef.current;
    const activeStart = swiper.activeIndex;
    let newSelectorIndex = index - activeStart;

    const isLastInGroup = newSelectorIndex === groupSize - 1;
    const isFirstInGroup = newSelectorIndex === 0;
    const remaining = totalSlides % groupSize;

    setSlideChangedViaClick(true);

    if (isLastInGroup) {
      swiper.slideTo(activeStart + groupSize);
      newSelectorIndex = index - remaining;
    } else if (isFirstInGroup && index !== 0) {
      swiper.slideTo(Math.max(swiper.activeIndex - groupSize, 0));
      newSelectorIndex = swiper.activeIndex + remaining;
    }

    setSelectorIndex(newSelectorIndex);
    setIndicatorInformationIndex(index);
    updateIndicatorPosition(newSelectorIndex, groupSize);
  }, [enableUserScrolling, groupSize, updateIndicatorPosition, totalSlides]);

  useEffect(() => {
    const swiper = swiperRef.current;
    if (!swiper) return;

    let prevActiveIndex = swiper.activeIndex;

    const handleSlideChange = () => {
      if (slideChangedViaClick) {
        setSlideChangedViaClick(false);
        return;
      }
      const currentIndex = swiper.activeIndex;
      const moved = currentIndex - prevActiveIndex;
      prevActiveIndex = currentIndex;

      const newSelectorIndex = Math.max(0, Math.min(groupSize - 1, selectorIndex - moved));
      const newInfoIndex = currentIndex + newSelectorIndex;
      if (!slideChangedViaClick) {
        setSelectorIndex(newSelectorIndex);
        setIndicatorInformationIndex(Math.min(newInfoIndex, totalSlides - 1));
        updateIndicatorPosition(newSelectorIndex, groupSize);
      }
    };

    swiper.on('slideChange', handleSlideChange);

    // only fire when a slide actually changes
    if (prevActiveIndex !== swiper.activeIndex) {
      handleSlideChange();
    }

    // eslint-disable-next-line consistent-return
    return () => {
      swiper.off('slideChange', handleSlideChange);
    };
  }, [selectorIndex, totalSlides, updateIndicatorPosition, slideChangedViaClick, groupSize]);

  // handle pointer events
  useEffect(() => {
    if (isDragging) {
      window.addEventListener('pointermove', handlePointerMove);
      window.addEventListener('pointerup', handlePointerUp);
    } else {
      window.removeEventListener('pointermove', handlePointerMove);
      window.removeEventListener('pointerup', handlePointerUp);
    }

    return () => {
      window.removeEventListener('pointermove', handlePointerMove);
      window.removeEventListener('pointerup', handlePointerUp);
    };
  }, [isDragging, handlePointerMove, handlePointerUp]);

  useEffect(() => {
    const initialIndex = activeShowcaseItem - 1;
    const initialSelectorIndex = initialIndex % groupSize;
    setSelectorIndex(initialSelectorIndex);
    setIndicatorInformationIndex(initialIndex);
    updateIndicatorPosition(initialSelectorIndex, groupSize);

    if (isMobile && swiperRef.current) {
      const swiper = swiperRef.current;
      const centerIndexInGroup = Math.floor(groupSize / 2);

      const maxStartIndex = Math.max(0, totalSlides - groupSize);
      const desiredStartIndex = Math.min(
        Math.max(0, initialIndex - centerIndexInGroup),
        maxStartIndex,
      );

      swiper.slideTo(desiredStartIndex);

      const newSelectorIndex = initialIndex - desiredStartIndex;
      setSelectorIndex(newSelectorIndex);
      updateIndicatorPosition(newSelectorIndex, groupSize);
    }
  }, [activeShowcaseItem, groupSize, updateIndicatorPosition, isMobile, totalSlides]);

  return (
    <StyledOrderedShowcase className={className}>
      <StyledSwiper
        id={`unique-showcase-${uniqueId}`}
        onBeforeInit={(swiper) => {
          swiperRef.current = swiper;
        }}
        modules={[Navigation, A11y, Controller]}
        slidesPerView={groupSize}
        slidesPerGroup={1}
        controller={{ control: swiperRef.current }}
        allowTouchMove={false}
        spaceBetween={(is5050 || isMobile) ? 10 : 15}
      >
        {orderedShowcaseItems.map((item, idx) => (
          <StyledSwiperSlide key={item.id}>
            <ItemWrapper>
              <OrderedShowcaseItem
                {...item}
                active={indicatorInformationIndex === idx}
                onClick={() => handleItemClick(idx)}
                clickable={enableUserScrolling}
                itemSizeLimit={itemSizeLimit}
              />
            </ItemWrapper>
          </StyledSwiperSlide>
        ))}
      </StyledSwiper>

      <IndicatorBar ref={scrollbarRef} $is5050={is5050}>
        <BarElement />
        <IndicatorIconWrapper
          onPointerDown={handlePointerDown}
          $is5050={is5050}
          $indicatorWidth={indicatorWidth}
          $indicatorOffsetLeft={indicatorLeft}
          $grabbable={enableUserScrolling}
        >
          <IndicatorIcon name={`loader-${property}`} $is5050={is5050} />
          <IndicatorText $colorProfile={parentColorProfile} $is5050={is5050}>
            {orderedShowcaseItems[indicatorInformationIndex]?.title}
          </IndicatorText>
          {orderedShowcaseItems[indicatorInformationIndex]?.relationships?.button && (
            <IndicatorButton
              href={orderedShowcaseItems[indicatorInformationIndex]
                ?.relationships
                ?.button
                ?.buttonLink}
            >
              {orderedShowcaseItems[indicatorInformationIndex]?.relationships?.button?.buttonText}
            </IndicatorButton>
          )}
        </IndicatorIconWrapper>
      </IndicatorBar>
    </StyledOrderedShowcase>
  );
};

OrderedShowcase.propTypes = {
  className: PropTypes.string,
  activeShowcaseItem: PropTypes.number,
  enableUserScrolling: PropTypes.bool,
  is5050: PropTypes.bool,
  relationships: PropTypes.shape({
    showcaseItems: PropTypes.arrayOf(PropTypes.shape({})),
  }),
  parentColorProfile: PropTypes.string,
  itemSizeLimit: PropTypes.number,
};
